import { useEffect, useState } from "react";
import { t } from "i18next";
import { Avatar, Button, Modal, Space, Tag, Tooltip, Typography } from "antd";
import {
    UserOutlined,
    SendOutlined,
    FileTextOutlined,
    MedicineBoxOutlined,
    ApiOutlined,
    EyeOutlined,
    CloudSyncOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    ThunderboltOutlined,
    SyncOutlined,
    PictureOutlined,
    QuestionCircleOutlined,
    ClockCircleOutlined,
    WarningOutlined,
    ExclamationCircleOutlined,
    StopOutlined
} from "@ant-design/icons";
import moment from "moment";
import DataGrid from "../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import Helper from "../../core/Helper";
import ApiService from "../../core/service/ApiService";
import EnabizOperationDetailModal from "./EnabizOperationDetailModal";
import EnabizTeletipStatusModal from "./EnabizTeletipStatusModal";
import EnabizRemoveKosModal from "./EnabizRemoveKosModal";
import EnabizCancelRequestModal from "./EnabizCancelRequestModal";

const { Text } = Typography;

function BlockersTooltip({ blockers, fallback }) {
    if (!blockers || blockers.length === 0) {
        return fallback || null
    }
    return (
        <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('Reason not sent')}:</div>
            <ul style={{ paddingLeft: 16, margin: 0 }}>
                {blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
        </div>
    )
}

function StatusTag({ status, sentAt, error, blockers, neverSentLabel }) {
    if (status === true) {
        return (
            <Tooltip title={sentAt ? moment(sentAt).format('DD.MM.YYYY HH:mm:ss') : null}>
                <Tag icon={<CheckCircleOutlined />} color="success">{t('Success')}</Tag>
            </Tooltip>
        )
    }
    if (status === false) {
        return (
            <Tooltip title={error || t('Failed')}>
                <Tag icon={<CloseCircleOutlined />} color="error">{t('Failed')}</Tag>
            </Tooltip>
        )
    }
    return (
        <Tooltip title={<BlockersTooltip blockers={blockers} />}>
            <Tag>{neverSentLabel || t('Not Sent')}</Tag>
        </Tooltip>
    )
}

// TeletipMatchState (backend HIS.Data.Enums.TeletipMatchState) — sayılar iki tarafta aynı.
const MATCH_STATE = {
    NOT_QUERIED: 0,
    PENDING: 1,
    MATCHED: 2,
    NOT_FOUND: 3,
    FAILED: 4,
    GIVEN_UP: 5,
    QUERY_ERROR: 6
}

const MATCH_STATE_META = {
    [MATCH_STATE.NOT_QUERIED]: { color: 'default', icon: <QuestionCircleOutlined />, label: 'Not Queried' },
    [MATCH_STATE.PENDING]: { color: 'processing', icon: <ClockCircleOutlined />, label: 'Waiting For Image' },
    [MATCH_STATE.MATCHED]: { color: 'success', icon: <CheckCircleOutlined />, label: 'Image Matched' },
    [MATCH_STATE.NOT_FOUND]: { color: 'error', icon: <ExclamationCircleOutlined />, label: 'No Record In Teletip' },
    [MATCH_STATE.FAILED]: { color: 'error', icon: <CloseCircleOutlined />, label: 'Match Failed' },
    [MATCH_STATE.GIVEN_UP]: { color: 'error', icon: <WarningOutlined />, label: 'Match Timed Out' },
    [MATCH_STATE.QUERY_ERROR]: { color: 'warning', icon: <WarningOutlined />, label: 'Could Not Query' }
}

// RisJobState (backend HIS.Data.Enums.RisJobState) — RIS'teki KOS iş kaydının akıbeti.
const RIS_JOB_STATE_LABEL = {
    0: 'RIS Job Pending',
    1: 'RIS Job Completed',
    2: 'RIS Job Error',
    3: 'RIS Job Waiting Study',
    4: 'RIS Job Expired',
    99: 'RIS Job Unknown'
}

const fmtDate = (d) => d ? moment(d).format('DD.MM.YYYY HH:mm:ss') : '-'

/**
 * Görüntü eşleşme kolonu. Tooltip'te sorgu geçmişi, ham Teletip durumları ve — en önemlisi —
 * RIS'ten gelen kök sebep gösterilir: "istek gitti ama KOS oluşmadı" durumunun nedeni burada.
 */
function MatchStateTag({ row }) {
    const meta = MATCH_STATE_META[row.teletipMatchState] || MATCH_STATE_META[MATCH_STATE.NOT_QUERIED];

    const tooltip = (
        <div style={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t(meta.label)}</div>

            {row.teletipMatchState === MATCH_STATE.NOT_FOUND && (
                <div style={{ marginBottom: 6 }}>
                    {t('Teletip does not know this accession — the study may have been acquired with a wrong patient id or accession number.')}
                </div>
            )}

            <div>{t('Last Query')}: {fmtDate(row.teletipLastCheckDate)}</div>
            <div>{t('Query Count')}: {row.teletipCheckCount ?? 0}</div>
            {row.teletipNextCheckDate && <div>{t('Next Query')}: {fmtDate(row.teletipNextCheckDate)}</div>}
            {row.teletipMatchedDate && <div>{t('Matched At')}: {fmtDate(row.teletipMatchedDate)}</div>}

            {(row.teletipWadoStatus || row.teletipStatus || row.teletipMedulaStatus || row.teletipReportStatus) && (
                <div style={{ marginTop: 6 }}>
                    {row.teletipWadoStatus && <div>WADO: {row.teletipWadoStatus}</div>}
                    {row.teletipStatus && <div>Teletip: {row.teletipStatus}</div>}
                    {row.teletipMedulaStatus && <div>Medula: {row.teletipMedulaStatus}</div>}
                    {row.teletipReportStatus && <div>{t('Report')}: {row.teletipReportStatus}</div>}
                    {row.teletipWadoTestCount != null && <div>{t('Teletip WADO Attempts')}: {row.teletipWadoTestCount}</div>}
                </div>
            )}

            {row.risJobState != null && (
                <div style={{ marginTop: 6 }}>
                    <div>{t('RIS Job')}: {t(RIS_JOB_STATE_LABEL[row.risJobState] || '-')}</div>
                    {row.risJobMessage && <div>{row.risJobMessage}</div>}
                </div>
            )}

            {row.teletipLastMessage && (
                <div style={{ marginTop: 6 }}>{row.teletipLastMessage}</div>
            )}
        </div>
    )

    return (
        <Tooltip title={tooltip}>
            <Tag icon={meta.icon} color={meta.color}>{t(meta.label)}</Tag>
        </Tooltip>
    )
}

export default function EnabizOperationDataGrid() {
    const {
        queryModel, setDataGridProps, setData, setPaginationResult, refreshDataGrid,
        selectedRows, setSelectedRows
    } = useDataGrid();

    const [detail, setDetail] = useState({ open: false, messageId: null });
    const [teletipStatus, setTeletipStatus] = useState({ open: false, appointmentId: null, accessionNumber: null });
    const [removeKos, setRemoveKos] = useState({ open: false, row: null });
    const [cancelRequest, setCancelRequest] = useState({ open: false, row: null });
    const [querying, setQuerying] = useState(false);

    const handleSend = (action, row) => {
        const titles = {
            ris: t('Send RIS Teletip Job?'),
            request: t('Send Request HL7?'),
            report: t('Send Report HL7?'),
            risHl7: t('Force send RIS HL7?'),
        }
        const contents = {
            risHl7: t('All preconditions will be skipped. Continue?'),
        }
        Modal.confirm({
            title: titles[action],
            content: (
                <>
                    <div>{`${row.patientName} ${row.patientLastname} - ${row.operationName} (${row.accessionNumber || '-'})`}</div>
                    {contents[action] && <div style={{ marginTop: 8, color: '#d48806' }}>{contents[action]}</div>}
                </>
            ),
            okText: t('Send'),
            cancelText: t('Cancel'),
            onOk: async () => {
                let res
                if (action === 'ris') res = await ApiService.EnabizOperation.SendRisJob(row.id)
                else if (action === 'request') res = await ApiService.EnabizOperation.SendRequestHl7(row.id)
                else if (action === 'report') res = await ApiService.EnabizOperation.SendReportHl7(row.id)
                else if (action === 'risHl7') res = await ApiService.EnabizOperation.SendRisHl7(row.id)
                if (res && res.success) refreshDataGrid()
            }
        })
    }

    // Sonucu KAYDEDEN sorgu. Aksiyon çubuğundaki "Teletip durumu" butonu ise anlık bakıştır
    // (AppointmentTeletip/GetTeletipOrderStatus), hiçbir şey yazmaz.
    const handleQueryMatch = async (row) => {
        setQuerying(true)
        try {
            const res = await ApiService.EnabizOperation.QueryTeletipStatus(row.id)
            if (res && res.success) refreshDataGrid()
        } finally {
            setQuerying(false)
        }
    }

    const handleQueryMatchBulk = () => {
        const ids = (selectedRows || []).map(x => x.id)
        if (ids.length === 0) return

        Modal.confirm({
            title: t('Query image match status for selected rows?'),
            content: `${ids.length} ${t('records')}`,
            okText: t('Query'),
            cancelText: t('Cancel'),
            onOk: async () => {
                setQuerying(true)
                try {
                    const res = await ApiService.EnabizOperation.QueryTeletipStatusBulk(ids)
                    if (res && res.success) {
                        setSelectedRows([])
                        refreshDataGrid()
                    }
                } finally {
                    setQuerying(false)
                }
            }
        })
    }

    useEffect(() => {
        setDataGridProps({
            rowKey: 'id',
            columns: [
                {
                    key: 'appointmentId', dataIndex: 'appointmentId', title: t('Apt Id'), width: 90,
                    render: (val, row) => (
                        <div>
                            <Text strong>#{val}</Text>
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{moment(row.appointmentStartDate).format('DD.MM.YYYY HH:mm')}</div>
                        </div>
                    )
                },
                {
                    key: 'patientName', dataIndex: 'patientName', title: t('Patient'),
                    render: (_, row) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar size={24} icon={<UserOutlined />} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Text strong>{row.patientName} {row.patientLastname}</Text>
                                <Text type="secondary" style={{ fontSize: 11 }}>{row.patientCustomId || '-'}</Text>
                            </div>
                        </div>
                    )
                },
                {
                    key: 'operationName', dataIndex: 'operationName', title: t('Operation'),
                    render: (val, row) => (
                        <Space size={4}>
                            <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                            <div>
                                <div>{val}</div>
                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                    {row.modality || '-'} {row.sutCode ? `• ${row.sutCode}` : ''}
                                </div>
                            </div>
                        </Space>
                    )
                },
                {
                    key: 'accessionNumber', dataIndex: 'accessionNumber', title: t('Accession No'), width: 140,
                    render: (val) => val ? <Text code>{val}</Text> : '-'
                },
                {
                    key: 'unitName', dataIndex: 'unitName', title: t('Unit'), width: 130,
                    render: (val) => <Text style={{ fontSize: 12 }}>{val}</Text>
                },
                {
                    key: 'institutionName', dataIndex: 'institutionName', title: t('Institution'), width: 140,
                    render: (val) => <Tooltip title={val}><Text ellipsis style={{ maxWidth: 130, fontSize: 12 }}>{val}</Text></Tooltip>
                },
                {
                    key: 'risHl7Sent', dataIndex: 'risHl7Sent', title: t('RIS HL7'), width: 130,
                    render: (val, row) => val
                        ? <Tooltip title={(
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                {row.risHl7SentDate ? moment(row.risHl7SentDate).format('DD.MM.YYYY HH:mm:ss') : null}
                                {row.risHl7AckMessage ? `\nACK: ${row.risHl7AckMessage}` : ''}
                            </div>
                        )}>
                            <Tag icon={<CheckCircleOutlined />} color="success">{t('Sent')}</Tag>
                        </Tooltip>
                        : <Tooltip title={<BlockersTooltip blockers={row.risHl7Blockers} />}>
                            <Tag>{t('Not Sent')}</Tag>
                        </Tooltip>
                },
                {
                    // DİKKAT: bu kolon "istek RIS'e gitti mi"yi gösterir, "KOS oluştu mu"yu değil.
                    // Gerçek akıbet RIS job durumu (tooltip) ve Görüntü Eşleşme kolonundadır.
                    key: 'risJobSuccess', dataIndex: 'risJobSuccess', title: t('RIS KOS'), width: 150,
                    render: (val, row) => (
                        <Space direction="vertical" size={0}>
                            <Tooltip title={t('This only means the request reached RIS. Whether the KOS was actually created is shown by the RIS job state below and the Image Match column.')}>
                                <span>
                                    <StatusTag
                                        status={val}
                                        sentAt={row.risJobSentDate}
                                        error={row.risJobError}
                                        blockers={row.risJobBlockers}
                                    />
                                </span>
                            </Tooltip>
                            {row.risJobState != null && (
                                <Tooltip title={row.risJobMessage || null}>
                                    <Text
                                        type={[2, 3, 4].includes(row.risJobState) ? 'danger' : 'secondary'}
                                        style={{ fontSize: 11 }}
                                    >
                                        {t(RIS_JOB_STATE_LABEL[row.risJobState] || '-')}
                                    </Text>
                                </Tooltip>
                            )}
                        </Space>
                    )
                },
                {
                    key: 'teletipMatchState', dataIndex: 'teletipMatchState', title: t('Image Match'), width: 165,
                    render: (_, row) => <MatchStateTag row={row} />
                },
                {
                    key: 'requestHl7Success', dataIndex: 'requestHl7Success', title: t('Istem (Teletip)'), width: 160,
                    render: (val, row) => (
                        <Space direction="vertical" size={0}>
                            <StatusTag
                                status={val}
                                sentAt={row.requestHl7SentAt}
                                error={row.requestHl7Error}
                                blockers={row.requestHl7Blockers}
                            />
                            {/* İptal, istemin kendi satırında izleniyor: başarılıysa istem
                                bakanlıktan geri çekilmiş demektir. */}
                            {row.requestCancelSuccess === true && (
                                <Tooltip title={(
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                        {t('Cancelled At')}: {fmtDate(row.requestCancelSentAt)}
                                        {row.requestCancelReason ? `\n${t('Reason')}: ${row.requestCancelReason}` : ''}
                                    </div>
                                )}>
                                    <Tag icon={<StopOutlined />} color="warning" style={{ marginTop: 2 }}>
                                        {t('Cancelled')}
                                    </Tag>
                                </Tooltip>
                            )}
                            {row.requestCancelSuccess === false && (
                                <Tooltip title={row.requestCancelError || t('Cancel Failed')}>
                                    <Tag icon={<CloseCircleOutlined />} color="error" style={{ marginTop: 2 }}>
                                        {t('Cancel Failed')}
                                    </Tag>
                                </Tooltip>
                            )}
                        </Space>
                    )
                },
                {
                    key: 'reportHl7Success', dataIndex: 'reportHl7Success', title: t('Report (Teletıp)'), width: 150,
                    render: (val, row) => (
                        <StatusTag
                            status={val}
                            sentAt={row.reportHl7SentAt}
                            error={row.reportHl7Error}
                            blockers={row.reportHl7Blockers}
                        />
                    )
                },
                {
                    key: 'actions', title: t('Actions'), fixed: 'right', width: 345,
                    render: (_, row) => (
                        <Space size={4} wrap>
                            <Tooltip title={t('Query image match now (result is saved)')}>
                                <Button
                                    size="small"
                                    type="primary"
                                    ghost
                                    icon={<SyncOutlined />}
                                    disabled={!row.accessionNumber}
                                    onClick={() => handleQueryMatch(row)}
                                />
                            </Tooltip>
                            <Tooltip title={t('Force send RIS HL7 (skip all preconditions)')}>
                                <Button size="small" danger icon={<ThunderboltOutlined />} onClick={() => handleSend('risHl7', row)} />
                            </Tooltip>
                            <Tooltip title={t('Send RIS Teletip Job')}>
                                <Button size="small" icon={<ApiOutlined />} onClick={() => handleSend('ris', row)} />
                            </Tooltip>
                            <Tooltip title={t('Send Request HL7')}>
                                <Button size="small" icon={<SendOutlined />} onClick={() => handleSend('request', row)} />
                            </Tooltip>
                            <Tooltip title={t('Send Report HL7')}>
                                <Button size="small" icon={<FileTextOutlined />} onClick={() => handleSend('report', row)} />
                            </Tooltip>
                            <Tooltip title={t('HL7 Message Detail')}>
                                <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    disabled={!row.requestHl7MessageId}
                                    onClick={() => setDetail({ open: true, messageId: row.requestHl7MessageId })}
                                />
                            </Tooltip>
                            <Tooltip title={t('Query Teletip Status')}>
                                <Button
                                    size="small"
                                    icon={<CloudSyncOutlined />}
                                    disabled={!row.accessionNumber}
                                    onClick={() => setTeletipStatus({
                                        open: true,
                                        appointmentId: row.appointmentId,
                                        accessionNumber: row.accessionNumber,
                                        lastCheckDate: row.teletipLastCheckDate
                                    })}
                                />
                            </Tooltip>
                            <Tooltip title={
                                row.requestCancelSuccess === true
                                    ? t('This request has already been cancelled.')
                                    : row.requestHl7Success !== true
                                        ? t('There is no successfully sent request to cancel.')
                                        : t('Cancel Request (Teletip)')
                            }>
                                <Button
                                    size="small"
                                    danger
                                    icon={<StopOutlined />}
                                    disabled={row.requestHl7Success !== true || row.requestCancelSuccess === true}
                                    onClick={() => setCancelRequest({ open: true, row })}
                                />
                            </Tooltip>
                            <Tooltip title={
                                row.teletipMatchState === MATCH_STATE.NOT_FOUND
                                    ? t('Teletip has no record for this accession — there is no KOS to remove.')
                                    : t('Remove KOS')
                            }>
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    disabled={!row.accessionNumber || row.teletipMatchState === MATCH_STATE.NOT_FOUND}
                                    onClick={() => setRemoveKos({ open: true, row })}
                                />
                            </Tooltip>
                        </Space>
                    )
                }
            ],
            enableRowSelection: true,
            antdTableProps: {
                scroll: { x: 1950 },
                size: 'middle'
            }
        })
    }, [])

    const load = async () => {
        const input = {
            ...queryModel,
            filter: {
                ...queryModel.filter,
                startDate: queryModel.filter?.startDate ? Helper.Date.beginOfDay(queryModel.filter.startDate) : null,
                endDate: queryModel.filter?.endDate ? Helper.Date.endOfDay(queryModel.filter.endDate) : null,
            }
        }
        const res = await ApiService.EnabizOperation.GetEnabizOperationDataGrid(input);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination)
        }
    }

    useEffect(() => {
        load()
    }, [queryModel.updateKey])

    return (
        <>
            <Space style={{ marginBottom: 8 }}>
                <Button
                    type="primary"
                    icon={<PictureOutlined />}
                    size="small"
                    loading={querying}
                    disabled={!selectedRows || selectedRows.length === 0}
                    onClick={handleQueryMatchBulk}
                >
                    {t('Query Image Match For Selected')}
                    {selectedRows?.length > 0 ? ` (${selectedRows.length})` : ''}
                </Button>
            </Space>
            <DataGrid />
            <EnabizOperationDetailModal
                open={detail.open}
                messageId={detail.messageId}
                onClose={() => setDetail({ open: false, messageId: null })}
            />
            <EnabizTeletipStatusModal
                open={teletipStatus.open}
                appointmentId={teletipStatus.appointmentId}
                accessionNumber={teletipStatus.accessionNumber}
                lastCheckDate={teletipStatus.lastCheckDate}
                onClose={() => setTeletipStatus({ open: false, appointmentId: null, accessionNumber: null })}
            />
            <EnabizRemoveKosModal
                open={removeKos.open}
                row={removeKos.row}
                onClose={() => setRemoveKos({ open: false, row: null })}
                onSuccess={refreshDataGrid}
            />
            <EnabizCancelRequestModal
                open={cancelRequest.open}
                row={cancelRequest.row}
                onClose={() => setCancelRequest({ open: false, row: null })}
                onSuccess={refreshDataGrid}
            />
        </>
    )
}
